import { useSelector } from "react-redux";
import {
  ASSIGNED,
  AUTOMATED,
  NO_TAGS,
  NO_TICKET_INFO,
  SNOOZED_TICKET_INFO,
  ticketPhases,
  UNDISCOVERED,
} from "components/dashboard/AgentInbox/enum";

const { ALL, ISSUE_DISCOVERY, PROBLEM_CONFIRMATION, SOLUTION_DELIVERY } = ticketPhases;

export const useDoesTicketMatchFilters = () => {
  const { userId } = useSelector((state) => state.auth?.user);
  
  const {
    ticketPhase: currentFilterTicketPhase,
    messageTagId: filterMessageTagId,
    ticketInfoValue,
    classification: filterClassification,
    isMentioned,
    followUpActive,
    ticketSource,
    search,
    agentId,
  } = useSelector((state) => state?.ticketsFilters.ticketsfilterRequest);

  // Return a function that can be used directly with .filter()
  return (ticket) => {
    const actingId = agentId || userId;

    const filterByTicketPhase = () => {
      if (!currentFilterTicketPhase || currentFilterTicketPhase === ALL) {
        return true;
      }

      if (
        (currentFilterTicketPhase === SOLUTION_DELIVERY ||
          currentFilterTicketPhase === PROBLEM_CONFIRMATION) &&
        ticket?.followUpActive
      ) {
        return false;
      }

      return ticket.ticketPhase === currentFilterTicketPhase;
    };

    const filterByMessageTag = () => {
      if (currentFilterTicketPhase !== ISSUE_DISCOVERY) {
        return true;
      }

      if (!filterMessageTagId) {
        return true;
      }

      if (filterMessageTagId === UNDISCOVERED) {
        return !ticket.issueDiscovered;
      }

      if (filterMessageTagId === NO_TAGS) {
        return (
          (ticket.messageTags === null || ticket.messageTags === "") &&
          ticket.issueDiscovered === true
        );
      }

      return (
        ticket.issueDiscovered &&
        ticket.messageTags?.split(",")?.includes(filterMessageTagId)
      );
    };

    const filterByTicketInfoValue = () => {
      let isAssigned;

      if (!ticketInfoValue) return false;

      if (ticket?.assigned) {
        if (agentId) {
          isAssigned = ticket?.assignedTeamMemberId === agentId;
        } else {
          isAssigned = ticket?.assignedTeamMemberId === userId;
        }
      }

      let infoIndex = ticket?.info?.findIndex(
        (el) => el.ticketInfoValue === ticketInfoValue
      );

      if (ticket.ticketPhase === ISSUE_DISCOVERY) {
        return true;
      }

      if (!ticketInfoValue) {
        return isMentioned || followUpActive ? true : isAssigned;
      }

      if (ticketInfoValue === AUTOMATED) {
        return !ticket?.assigned;
      }

      if (ticketInfoValue === ASSIGNED) {
        return ticket?.assigned || ticket?.issueDiscovered === true;
      }

      if (ticketInfoValue === NO_TICKET_INFO) {
        return ticket?.info === null || ticket?.info?.length < 1;
      }

      if (ticketInfoValue === SNOOZED_TICKET_INFO) {
        return ticket?.snoozed;
      }

      const belongsToAgent = isMentioned ? true : isAssigned;
      return belongsToAgent && infoIndex !== -1;
    };

    const filterByClassification = () => {
      if (ticket.ticketPhase === ISSUE_DISCOVERY) {
        return true;
      }

      if (!filterClassification) {
        return true;
      }

      return ticket.ticketClassification === filterClassification;
    };

    const filterByMentions = () => {
      if (isMentioned !== true) {
        return true;
      }

      if (
        ticket?.commentResolved?.[0] &&
        ticket?.commentResolved?.[1] === actingId
      ) {
        return false;
      }

      let userIdList = [];
      if (ticket?.comments) {
        const mentioned = ticket?.comments?.map(
          (comment) => comment?.mentioned
        );

        const allMentioned = mentioned?.flat();
        userIdList = allMentioned?.map((mention) => mention?.userId);
      }

      return ticket?.comments
        ? userIdList?.includes(actingId)
        : ticket?.mentioned?.includes(actingId);
    };

    const filterByTicketStatus = () => {
      return ticket.ticketStatus;
    };

    const filterByTicketSource = () => {
      if (!ticketSource) {
        return true;
      }

      return ticket?.ticketSource === ticketSource;
    };

    const filterBySearch = () => {
      if (!search) {
        return true;
      }

      const {
        customer: { email, appMessageUserId },
      } = ticket || {};

      return (
        email?.toLowerCase()?.includes(search?.toLowerCase()) ||
        appMessageUserId?.toLowerCase()?.includes(search?.toLowerCase())
      );
    };

    // Apply all filters with the same logic as the component
    return (
      filterByTicketPhase() &&
      filterByMessageTag() &&
      filterByMentions() &&
      filterByTicketInfoValue() &&
      filterByClassification() &&
      filterByTicketSource() &&
      filterByTicketStatus() &&
      filterBySearch()
    );
  };
};
